/**
 * Dawn Orbit constellation — one composition: sky + three rings + one thread.
 */
import React from 'react';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';

const NIGHT = '#07111f';
const STAR = '#f5d08a';
const RING = 'rgba(245, 208, 138, 0.35)';
const MIST = 'rgba(148, 197, 210, 0.12)';

export default function DeskOrbitSky({ orbit }) {
  if (!orbit?.sky && !orbit?.thread) return null;
  const sky = orbit.sky || {};
  const rings = orbit.constellation?.rings || [];
  const tech = (orbit.tech || []).slice(0, 4);
  const sacred = orbit.sacred || {};
  const localEvents = (orbit.local?.events || []).slice(0, 3);
  const bg = sky.mediaType === 'image' && sky.url
    ? `linear-gradient(180deg, rgba(7,17,31,0.55) 0%, rgba(7,17,31,0.92) 70%), url(${sky.url}) center/cover no-repeat`
    : `radial-gradient(900px 420px at 70% 0%, ${MIST}, transparent), radial-gradient(600px 300px at 20% 30%, rgba(245,208,138,0.08), transparent), ${NIGHT}`;

  return (
    <Box
      data-testid="desk-orbit-sky"
      sx={{
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        minHeight: { xs: 360, sm: 420 },
        color: '#e8eef5',
        background: bg,
        border: '1px solid rgba(245,208,138,0.22)',
        boxShadow: '0 24px 60px rgba(7,17,31,0.35)',
        mb: 2,
      }}
    >
      <Box sx={{ p: { xs: 2.25, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Fraunces", "Iowan Old Style", Georgia, serif',
            fontWeight: 700,
            fontSize: { xs: '1.65rem', sm: '2rem' },
            letterSpacing: '-0.03em',
            color: STAR,
            mb: 0.35,
          }}
        >
          Dawn Orbit
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mb: 1.5, maxWidth: 520 }}>
          {orbit.profile?.label || 'Desk profile'}
          {' · '}
          {orbit.day}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {rings.map((r) => (
            <Chip
              key={r.id}
              size="small"
              label={`${r.label} · ${r.count}`}
              sx={{
                bgcolor: 'rgba(7,17,31,0.55)',
                color: STAR,
                border: `1px solid ${RING}`,
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="overline" sx={{ color: STAR, letterSpacing: '0.12em' }}>
              Sky
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 650, lineHeight: 1.25, mb: 0.75 }}>
              {sky.title || 'Horizon'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.88, mb: 1, lineHeight: 1.45 }}>
              {sky.explanation || ''}
            </Typography>
            {sky.url && (
              <Link href={sky.url} target="_blank" rel="noopener noreferrer" sx={{ color: STAR }}>
                Open sky image
              </Link>
            )}
          </Box>

          <Stack spacing={1.25}>
            <Box>
              <Typography variant="overline" sx={{ color: STAR }}>Local satellites</Typography>
              {(localEvents.length ? localEvents : [{ summary: 'Open morning — no events sensed' }]).map((e) => (
                <Typography key={e.summary} variant="body2" display="block" sx={{ opacity: 0.9 }}>
                  · {e.summary}
                </Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: STAR }}>Tech comets</Typography>
              {tech.map((t) => (
                <Typography key={t.title} variant="body2" display="block" sx={{ opacity: 0.9 }}>
                  ·
                  {' '}
                  {t.url ? (
                    <Link href={t.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#c5e4ef' }}>
                      {t.title}
                    </Link>
                  ) : t.title}
                </Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: STAR }}>
                Sacred · {sacred.label || 'rhythm'}
              </Typography>
              {(sacred.items || []).slice(0, 5).map((i) => (
                <Typography key={`${i.name}-${i.time}`} variant="body2" display="block" sx={{ opacity: 0.9 }}>
                  ·
                  {' '}
                  <strong>{i.name}</strong>
                  {i.time ? ` — ${i.time}` : ''}
                </Typography>
              ))}
              {sacred.note && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', opacity: 0.85 }}>
                  {sacred.note}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        {orbit.thread && (
          <Box
            sx={{
              mt: 2.5,
              pt: 1.75,
              borderTop: `1px solid ${RING}`,
            }}
          >
            <Typography variant="overline" sx={{ color: STAR }}>One thread</Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: '1.05rem',
                lineHeight: 1.45,
                maxWidth: 640,
              }}
            >
              {orbit.thread}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
